import { useEffect, useState, useCallback, useRef, type ReactNode } from 'react';
import type { Socket } from 'socket.io-client';
import { io } from 'socket.io-client';
import { API_CONFIG } from '../config/api.config';
import { logger } from '../utils/logger';
import { type JoinResult, type SocketContextType, SocketContext } from './socketTypes';

const ACK_TIMEOUT_MS = 5000;
const BACKOFF_BASE_MS = 1000;
const BACKOFF_MAX_MS = 30000;
const RECONNECT_DELAY_MS = 2000;
const RECONNECT_DELAY_MAX_MS = 10000;
const MAX_DISCONNECT_DURATION_MS = 2 * 60 * 1000;

interface SocketProviderProps {
  children: ReactNode;
}

export function SocketProvider({ children }: SocketProviderProps) {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [hasConnectedOnce, setHasConnectedOnce] = useState(false);
  const [reconnectCount, setReconnectCount] = useState(0);
  const [recreateKey, setRecreateKey] = useState(0);
  const recreateAttemptRef = useRef(0);
  const recreateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track active rooms for automatic re-join on reconnect
  // Using ref to avoid stale closure issues in reconnect handler
  const activeRoomsRef = useRef<{
    jobs: Set<string>;
    containers: Set<string>;
    analyses: Set<string>;
    improvements: Set<string>;
    tasks: Set<string>;
  }>({
    jobs: new Set(),
    containers: new Set(),
    analyses: new Set(),
    improvements: new Set(),
    tasks: new Set(),
  });

  /**
   * Helper to emit with acknowledgment and timeout
   * Returns a promise that resolves with the ack response or times out
   */
  const emitWithAck = useCallback(
    (
      socketInstance: Socket,
      event: string,
      data: string,
      timeoutMs: number = ACK_TIMEOUT_MS,
    ): Promise<JoinResult> => {
      return new Promise((resolve) => {
        const timeout = setTimeout(() => {
          resolve({
            success: false,
            error: 'timeout',
            message: `No acknowledgment received within ${timeoutMs}ms`,
          });
        }, timeoutMs);

        socketInstance.emit(event, data, (response: JoinResult) => {
          clearTimeout(timeout);
          resolve(response);
        });
      });
    },
    [],
  );

  const triggerRecreate = useCallback(() => {
    if (recreateTimeoutRef.current) return;
    const attempt = recreateAttemptRef.current;
    const delay = Math.min(BACKOFF_BASE_MS * Math.pow(2, attempt), BACKOFF_MAX_MS);
    recreateAttemptRef.current = attempt + 1;
    recreateTimeoutRef.current = setTimeout(() => {
      recreateTimeoutRef.current = null;
      setRecreateKey((k) => k + 1);
    }, delay);
    logger.warn(
      `[SocketProvider] Scheduling socket recreate in ${delay}ms (attempt ${attempt + 1})`,
    );
  }, []);

  useEffect(() => {
    const socketUrl = API_CONFIG.socketUrl || window.location.origin;

    // Connect to socket server with enhanced error handling and connection state recovery
    // Use WebSocket-only transport to avoid session affinity issues with HTTP polling
    // across multiple K8s pods. WebSocket connections are inherently sticky once established.
    const socketInstance = io(socketUrl, {
      transports: ['websocket'], // Skip polling - fixes 400 errors on multi-pod K8s
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: RECONNECT_DELAY_MS,
      reconnectionDelayMax: RECONNECT_DELAY_MAX_MS,
      reconnectionAttempts: Infinity,
      timeout: API_CONFIG.timeout || 20000,
      forceNew: true,
      withCredentials: true,
      // @ts-expect-error connectionStateRecovery not yet in type definitions
      connectionStateRecovery: {
        maxDisconnectionDuration: MAX_DISCONNECT_DURATION_MS,
        skipMiddlewares: true,
      },
    });

    socketInstance.on('connect', () => {
      logger.info('[Socket] Connected', { socketId: socketInstance.id });
      setIsConnected(true);
      setHasConnectedOnce(true);
      recreateAttemptRef.current = 0;
    });

    socketInstance.on('disconnect', (reason) => {
      // Always log disconnects - critical for debugging
      logger.warn('[Socket] Disconnected', {
        reason,
        socketId: socketInstance.id,
      });
      setIsConnected(false);
    });

    socketInstance.on('connect_error', (error) => {
      // Always log connection errors for debugging
      logger.error('[Socket] Connection error', {
        message: error.message || String(error),
      });
      setIsConnected(false);
    });

    // Provide a small hook for diagnostics - log transport failure info
    socketInstance.io.on('packet', (packet) => {
      if (API_CONFIG.showDebugInfo && packet && packet.type === 0) {
        // 0 = open packet. Useful to see handshake info in development
        logger.debug('[SocketProvider] Socket packet open', { packet });
      }
    });

    socketInstance.on('reconnect', () => {
      logger.info('[Socket] Reconnected', { socketId: socketInstance.id });
      setIsConnected(true);
      // Increment reconnect count to trigger data refresh in components
      setReconnectCount((prev) => prev + 1);

      // Automatically re-join all active rooms after reconnection
      const rooms = activeRoomsRef.current;

      // Re-join all rooms with acknowledgment handling
      // Failed re-joins are logged but don't block - components will refetch data anyway
      const rejoinWithAck = async (event: string, id: string, roomType: string) => {
        return new Promise<void>((resolve) => {
          const timeout = setTimeout(() => {
            logger.warn(`[SocketProvider] Rejoin ${roomType} ${id} timed out`);
            resolve();
          }, ACK_TIMEOUT_MS);

          socketInstance.emit(event, id, (response: JoinResult) => {
            clearTimeout(timeout);
            if (!response.success) {
              logger.warn(`[SocketProvider] Failed to rejoin ${roomType} ${id}`, {
                error: response.error,
              });
            }
            resolve();
          });
        });
      };

      // Re-join all rooms concurrently
      const rejoinPromises: Promise<void>[] = [];

      rooms.jobs.forEach((jobId) => {
        rejoinPromises.push(rejoinWithAck('join-job', jobId, 'job'));
      });

      rooms.containers.forEach((containerId) => {
        rejoinPromises.push(rejoinWithAck('join-container', containerId, 'container'));
      });

      rooms.analyses.forEach((jobId) => {
        rejoinPromises.push(rejoinWithAck('join-analysis', jobId, 'analysis'));
      });

      rooms.improvements.forEach((jobId) => {
        rejoinPromises.push(rejoinWithAck('join-improvements', jobId, 'improvements'));
      });

      rooms.tasks.forEach((taskId) => {
        rejoinPromises.push(rejoinWithAck('join-task', taskId, 'task'));
      });

      // Wait for all rejoin attempts to complete (or timeout)
      Promise.all(rejoinPromises).catch((err) => {
        logger.error('[SocketProvider] Error during room rejoin', { error: err });
      });
    });

    socketInstance.io.on('reconnect_attempt', () => {
      logger.info('[Socket] Reconnect attempt', { socketId: socketInstance.id });
    });

    socketInstance.on('reconnect_error', (error) => {
      logger.error('[Socket] Reconnection error', {
        message: error.message || String(error),
      });
    });

    socketInstance.on('reconnect_failed', () => {
      logger.warn('[Socket] Reconnection failed - will recreate socket');
      setIsConnected(false);
      triggerRecreate();
    });

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !socketInstance.connected) {
        logger.warn('[Socket] Tab became visible with dead socket, recreating');
        triggerRecreate();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const handleOnline = () => {
      logger.info('[Socket] Network came back online', {
        connected: socketInstance.connected,
        socketId: socketInstance.id,
      });
      if (!socketInstance.connected) {
        triggerRecreate();
      }
    };
    window.addEventListener('online', handleOnline);

    // Demo-specific events
    socketInstance.on('demo-heartbeat', () => {
      // Demo heartbeat received
    });

    setSocket(socketInstance);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
      if (recreateTimeoutRef.current) {
        clearTimeout(recreateTimeoutRef.current);
        recreateTimeoutRef.current = null;
      }
      socketInstance.close();
    };
    // triggerRecreate is stable (defined via useCallback with [] deps) and only feeds recreateKey
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recreateKey]);

  const joinContainer = useCallback(
    async (containerId: string): Promise<JoinResult> => {
      if (!socket || !isConnected) {
        logger.warn('[SocketProvider] Cannot join container - socket not connected');
        return { success: false, error: 'not_connected', message: 'Socket not connected' };
      }

      const result = await emitWithAck(socket, 'join-container', containerId);

      if (result.success) {
        activeRoomsRef.current.containers.add(containerId);
      }

      return result;
    },
    [socket, isConnected, emitWithAck],
  );

  const leaveContainer = useCallback(
    (containerId: string) => {
      // Always remove from tracking, even if disconnected
      // This ensures we don't re-join a room the user explicitly left
      activeRoomsRef.current.containers.delete(containerId);

      if (socket && isConnected) {
        socket.emit('leave-container', containerId);
      }
    },
    [socket, isConnected],
  );

  const joinAnalysis = useCallback(
    async (jobId: string): Promise<JoinResult> => {
      if (!socket || !isConnected) {
        logger.warn('[SocketProvider] Cannot join analysis - socket not connected');
        return { success: false, error: 'not_connected', message: 'Socket not connected' };
      }

      const result = await emitWithAck(socket, 'join-analysis', jobId);

      if (result.success) {
        activeRoomsRef.current.analyses.add(jobId);
      }

      return result;
    },
    [socket, isConnected, emitWithAck],
  );

  const joinImprovements = useCallback(
    async (jobId: string): Promise<JoinResult> => {
      if (!socket || !isConnected) {
        return { success: false, error: 'not_connected', message: 'Socket not connected' };
      }

      const result = await emitWithAck(socket, 'join-improvements', jobId);

      if (result.success) {
        activeRoomsRef.current.improvements.add(jobId);
      }

      return result;
    },
    [socket, isConnected, emitWithAck],
  );

  const leaveAnalysis = useCallback(
    (jobId: string) => {
      // Always remove from tracking, even if disconnected
      // This ensures we don't re-join a room the user explicitly left
      activeRoomsRef.current.analyses.delete(jobId);

      if (socket && isConnected) {
        socket.emit('leave-analysis', jobId);
      }
    },
    [socket, isConnected],
  );

  const leaveImprovements = useCallback(
    (jobId: string) => {
      // Always remove from tracking, even if disconnected
      // This ensures we don't re-join a room the user explicitly left
      activeRoomsRef.current.improvements.delete(jobId);

      if (socket && isConnected) {
        socket.emit('leave-improvements', jobId);
      }
    },
    [socket, isConnected],
  );

  const joinJob = useCallback(
    async (jobId: string): Promise<JoinResult> => {
      if (!socket || !isConnected) {
        return { success: false, error: 'not_connected', message: 'Socket not connected' };
      }

      const result = await emitWithAck(socket, 'join-job', jobId);

      if (result.success) {
        activeRoomsRef.current.jobs.add(jobId);
      }

      return result;
    },
    [socket, isConnected, emitWithAck],
  );

  const leaveJob = useCallback(
    (jobId: string) => {
      // Always remove from tracking, even if disconnected
      // This ensures we don't re-join a room the user explicitly left
      activeRoomsRef.current.jobs.delete(jobId);

      if (socket && isConnected) {
        socket.emit('leave-job', jobId);
      }
    },
    [socket, isConnected],
  );

  const joinTask = useCallback(
    async (taskId: string): Promise<JoinResult> => {
      if (!socket || !isConnected) {
        return { success: false, error: 'not_connected', message: 'Socket not connected' };
      }

      const result = await emitWithAck(socket, 'join-task', taskId);

      if (result.success) {
        activeRoomsRef.current.tasks.add(taskId);
      }

      return result;
    },
    [socket, isConnected, emitWithAck],
  );

  const leaveTask = useCallback(
    (taskId: string) => {
      activeRoomsRef.current.tasks.delete(taskId);

      if (socket && isConnected) {
        socket.emit('leave-task', taskId);
      }
    },
    [socket, isConnected],
  );

  const value: SocketContextType = {
    socket,
    isConnected,
    hasConnectedOnce,
    reconnectCount,
    joinContainer,
    leaveContainer,
    joinAnalysis,
    leaveAnalysis,
    joinImprovements,
    leaveImprovements,
    joinJob,
    leaveJob,
    joinTask,
    leaveTask,
  };

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
}
